import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import {KeyAutocomplete} from './KeyAutocomplete';

import {
  ProjectKeysStatusesPerLang,
  ScreenshotKeyConnection,
  ScreenshotKeyConnectionStyles,
  ScreenshotKeyConnectionTextAlign,
} from '@bumpy/i18n_common';

type Props = {
  activateColorPick: (callback: (color: string) => void) => void;
  connection: ScreenshotKeyConnection;
  focused: boolean;
  selected?: string;
  label: string;
  keysStatusesPerLang: ProjectKeysStatusesPerLang;
  onFocus: () => void;
  onBlur: () => void;
  onDelete: () => void;
  onChange: (connection: ScreenshotKeyConnection) => void;
};

export const defaultStyles: ScreenshotKeyConnectionStyles = {
  background: '#ffffff',
  color: '#000000',
  fontStyle: 'normal',
  fontWeight: 'normal',
  fontSize: 14,
  textAlign: 'center',
};

export const ScreenshotKeyConnectionView: React.FC<Props> = (props) => {
  const styles = props.connection.styles || defaultStyles;

  function onChangeStyle(style: Partial<ScreenshotKeyConnectionStyles>): void {
    props.onChange({
      ...props.connection,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      styles: {
        ...styles,
        ...style,
      },
    });
  }

  return (
    <Stack
      marginTop={2}
      gap={1}
      style={{
        borderColor: props.focused ? 'red' : '#ccc',
        borderWidth: 1,
        borderStyle: 'solid',
        padding: 10,
      }}
    >
      <Stack direction="row">
        <KeyAutocomplete
          label={props.label}
          selected={props.connection.key}
          keysStatusesPerLang={props.keysStatusesPerLang}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onSelect={(key) => {
            const connection = {
              ...props.connection,
              key,
            };
            if (!connection.styles) {
              connection.styles = defaultStyles;
            }
            props.onChange(connection);
          }}
        />
        <IconButton color="error" onClick={props.onDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack direction="row" gap={1}>
        <ColorInput
          activateColorPick={props.activateColorPick}
          label="Text color"
          value={styles.color}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onChange={(color) => {
            onChangeStyle({color});
          }}
        />
        <TextField
          fullWidth
          label="Font size"
          variant="standard"
          value={styles.fontSize}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onChange={(e) => {
            onChangeStyle({fontSize: Number(e.target.value)});
          }}
        />
        <FormControl fullWidth>
          <InputLabel variant="standard" id="fontstyle-label">
            Font style
          </InputLabel>
          <Select
            value={styles.fontStyle}
            variant="standard"
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onChange={(event) => {
              onChangeStyle({fontStyle: event.target.value});
            }}
          >
            <MenuItem value="normal">normal</MenuItem>
            <MenuItem value="italic">italic</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Stack direction="row" gap={1}>
        <ColorInput
          activateColorPick={props.activateColorPick}
          label="Background color"
          value={styles.background}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          onChange={(background) => {
            onChangeStyle({background});
          }}
        />
        <FormControl fullWidth>
          <InputLabel variant="standard" id="fontweight-label">
            Font weight
          </InputLabel>
          <Select
            value={styles.fontWeight}
            variant="standard"
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onChange={(event) => {
              onChangeStyle({fontWeight: event.target.value});
            }}
          >
            <MenuItem value="normal">normal</MenuItem>
            <MenuItem value="bold">bold</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel variant="standard" id="textAlign-label">
            Align
          </InputLabel>
          <Select
            fullWidth
            value={styles.textAlign}
            variant="standard"
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onChange={(event) => {
              onChangeStyle({
                textAlign: event.target
                  .value as ScreenshotKeyConnectionTextAlign,
              });
            }}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};

type ColorInputProps = {
  activateColorPick: (callback: (color: string) => void) => void;
  label: string;
  value: string;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (value: string) => void;
};

const ColorInput: React.FC<ColorInputProps> = (props) => {
  return (
    <TextField
      fullWidth
      label={props.label}
      variant="standard"
      value={props.value}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <div
              style={{
                width: 20,
                height: 20,
                borderWidth: 1,
                borderColor: '#7b7b7b',
                borderStyle: 'solid',
                background: props.value,
              }}
            />
          </InputAdornment>
        ),
      }}
      onFocus={() => {
        props.activateColorPick((color: string) => {
          props.onChange(color);
        });
        props.onFocus();
      }}
      onBlur={props.onBlur}
      onChange={(e) => {
        props.onChange(e.target.value);
      }}
    />
  );
};
